/**
 * Typed HTTP client for the chat worker.
 *
 * Every call that requires a session accepts a `token` argument so the caller
 * (a `Session` holder) decides when to refresh. No global state.
 */
import {
  canonicalChallenge,
  fromBase64Url,
  toBase64Url,
  type AuthChallenge,
  type ConversationId,
  type DeviceRegistration,
  type Envelope,
  type Invitation,
  type PublishedKeyPackage,
  type PushSubscriptionRegistration,
  type UserId,
} from '@chat/protocol';
import { ed25519 } from '@noble/curves/ed25519.js';
import type { Identity } from './identity.js';

export type { Identity };

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  constructor(status: number, message: string, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

/** Tunable jitter range in milliseconds applied before every outbound request. */
export interface JitterConfig {
  readonly minMs: number;
  readonly maxMs: number;
}

/**
 * Default jitter: 5–40ms uniform. Small enough not to matter for user-visible
 * latency, large enough to decorrelate request timing from user behaviour
 * (typing cadence, poll ticks) at the network observer level.
 */
export const DEFAULT_JITTER: JitterConfig = { minMs: 5, maxMs: 40 };

/** Disable jitter (tests, benchmarks). */
export const NO_JITTER: JitterConfig = { minMs: 0, maxMs: 0 };

function jitterDelay(config: JitterConfig): Promise<void> {
  const span = Math.max(0, config.maxMs - config.minMs);
  const delay = config.minMs + (span === 0 ? 0 : Math.floor(Math.random() * (span + 1)));
  if (delay <= 0) return Promise.resolve();
  return new Promise((resolve) => setTimeout(resolve, delay));
}

export interface ChatClientOptions {
  readonly fetchImpl?: typeof fetch;
  readonly jitter?: JitterConfig;
}

export class ChatClient {
  readonly #baseUrl: string;
  readonly #fetchInner: typeof fetch;
  readonly #jitter: JitterConfig;

  constructor(baseUrl: string, fetchImplOrOptions?: typeof fetch | ChatClientOptions) {
    this.#baseUrl = baseUrl.replace(/\/$/, '');
    const options =
      typeof fetchImplOrOptions === 'function'
        ? { fetchImpl: fetchImplOrOptions }
        : (fetchImplOrOptions ?? {});
    this.#fetchInner = options.fetchImpl ?? ((...args) => globalThis.fetch(...args));
    this.#jitter = options.jitter ?? DEFAULT_JITTER;
  }

  readonly #fetch: typeof fetch = async (input, init) => {
    await jitterDelay(this.#jitter);
    return this.#fetchInner(input, init);
  };

  async requestChallenge(): Promise<{ challenge: AuthChallenge; receipt: string }> {
    const res = await this.#fetch(`${this.#baseUrl}/auth/challenge`, { method: 'POST' });
    return this.#json(res);
  }

  async signIn(identity: Identity): Promise<string> {
    const { challenge, receipt } = await this.requestChallenge();
    // Challenge is signed by the auth key; identityPublicKey goes in the
    // body as a lookup hint (the server confirms the binding links the
    // two). Sensitive identity key is NOT used here.
    const signature = ed25519.sign(canonicalChallenge(challenge), identity.authPrivateKey);
    const res = await this.#fetch(`${this.#baseUrl}/auth/verify`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        challenge,
        receipt,
        response: {
          challenge: challenge.challenge,
          signature: toBase64Url(signature),
          publicKey: toBase64Url(identity.authPublicKey),
        },
        userId: identity.userId,
        deviceId: identity.deviceId,
        identityPublicKey: toBase64Url(identity.identityPublicKey),
      }),
    });
    const body = await this.#json<{ token: string }>(res);
    return body.token;
  }

  async registerDevice(token: string, registration: DeviceRegistration): Promise<void> {
    const res = await this.#fetch(`${this.#baseUrl}/devices`, {
      method: 'POST',
      headers: this.#authJson(token),
      body: JSON.stringify(registration),
    });
    await this.#json(res);
  }

  async publishKeyPackages(token: string, packages: PublishedKeyPackage[]): Promise<number> {
    const res = await this.#fetch(`${this.#baseUrl}/keypackages`, {
      method: 'POST',
      headers: this.#authJson(token),
      body: JSON.stringify(packages),
    });
    const body = await this.#json<{ added: number }>(res);
    return body.added;
  }

  async fetchVapidPublicKey(): Promise<string> {
    const res = await this.#fetch(`${this.#baseUrl}/push/vapid-public-key`);
    const body = await this.#json<{ publicKey: string }>(res);
    return body.publicKey;
  }

  async registerPushSubscription(
    token: string,
    registration: PushSubscriptionRegistration,
  ): Promise<void> {
    const res = await this.#fetch(`${this.#baseUrl}/push/subscriptions`, {
      method: 'POST',
      headers: this.#authJson(token),
      body: JSON.stringify(registration),
    });
    await this.#json(res);
  }

  async removePushSubscription(token: string): Promise<void> {
    const res = await this.#fetch(`${this.#baseUrl}/push/subscriptions`, {
      method: 'DELETE',
      headers: { authorization: `Bearer ${token}` },
    });
    await this.#json(res);
  }

  async sendInvitation(token: string, invitation: Invitation): Promise<void> {
    const res = await this.#fetch(`${this.#baseUrl}/invitations`, {
      method: 'POST',
      headers: this.#authJson(token),
      body: JSON.stringify(invitation),
    });
    await this.#json(res);
  }

  async fetchInvitations(token: string): Promise<Invitation[]> {
    const res = await this.#fetch(`${this.#baseUrl}/invitations`, {
      headers: { authorization: `Bearer ${token}` },
    });
    const body = await this.#json<{ invitations: Invitation[] }>(res);
    return body.invitations;
  }

  async takeKeyPackage(token: string, userId: UserId): Promise<PublishedKeyPackage | undefined> {
    const res = await this.#fetch(`${this.#baseUrl}/keypackages/${encodeURIComponent(userId)}`, {
      headers: { authorization: `Bearer ${token}` },
    });
    if (res.status === 404) {
      await res.text();
      return undefined;
    }
    return this.#json(res);
  }

  async sendMessage(
    token: string,
    conversationId: ConversationId,
    envelope: Envelope,
    recipients?: readonly UserId[],
    options?: { readonly silent?: boolean },
  ): Promise<number> {
    const body: Envelope & {
      recipients?: readonly UserId[];
      silent?: boolean;
    } = { ...envelope };
    if (recipients && recipients.length > 0) {
      body.recipients = recipients;
    }
    // `silent` tells the worker route to skip the push fan-out for
    // this envelope. Used for metadata that all peers will see when
    // they next open the app (reactions, pins, deletions, group
    // rename / avatar, MLS handshake commits, game moves) but that
    // doesn't deserve a "you have a new message" notification.
    // Real text / attachment / voice / video messages omit this so
    // they push as before.
    if (options?.silent) body.silent = true;
    const res = await this.#fetch(
      `${this.#baseUrl}/conversations/${encodeURIComponent(conversationId)}/send`,
      {
        method: 'POST',
        headers: this.#authJson(token),
        body: JSON.stringify(body),
      },
    );
    const data = await this.#json<{ sequence: number }>(res);
    return data.sequence;
  }

  async requestRegistration(email: string): Promise<{ status: string }> {
    const res = await this.#fetch(`${this.#baseUrl}/register`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    return this.#json(res);
  }

  async claim(payload: {
    token: string;
    userId: string;
    deviceId: string;
    identityPublicKey: string;
    authPublicKey: string;
  }): Promise<{ token: string; email: string }> {
    const res = await this.#fetch(`${this.#baseUrl}/auth/claim`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return this.#json(res);
  }

  /**
   * Rotate this identity's auth key (protocol v2). The caller must hold
   * the current identity private key to sign the rotation message. All
   * existing session tokens bound to the old auth key become unusable —
   * the client must re-sign-in via the challenge/verify flow.
   */
  async rotateAuthKey(payload: {
    identityPublicKey: string;
    newAuthPublicKey: string;
    issuedAt: number;
    signature: string;
  }): Promise<void> {
    const res = await this.#fetch(`${this.#baseUrl}/auth/rotate-auth`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
    });
    await this.#json(res);
  }

  async findMatch(
    token: string,
    engineId: string,
  ): Promise<{ status: 'matched' | 'waiting'; opponent?: UserId }> {
    const res = await this.#fetch(`${this.#baseUrl}/matchmaking`, {
      method: 'POST',
      headers: this.#authJson(token),
      body: JSON.stringify({ engineId }),
    });
    return this.#json(res);
  }

  async getMyProfile(token: string): Promise<{
    userId: string;
    username?: string;
    displayName?: string;
    avatarMediaId?: string;
  }> {
    const res = await this.#fetch(`${this.#baseUrl}/users/me`, {
      headers: { authorization: `Bearer ${token}` },
    });
    const body = await this.#json<{
      profile: {
        userId: string;
        username?: string;
        displayName?: string;
        avatarMediaId?: string;
      };
    }>(res);
    return body.profile;
  }

  /**
   * Set / update / clear the caller's username or display name.
   * Pass `null` for a field to clear it, omit to leave unchanged.
   */
  async setMyProfile(
    token: string,
    updates: {
      username?: string | null;
      displayName?: string | null;
      avatarMediaId?: string | null;
    },
  ): Promise<{
    userId: string;
    username?: string;
    displayName?: string;
    avatarMediaId?: string;
  }> {
    const res = await this.#fetch(`${this.#baseUrl}/users/profile`, {
      method: 'POST',
      headers: this.#authJson(token),
      body: JSON.stringify(updates),
    });
    const body = await this.#json<{
      profile: {
        userId: string;
        username?: string;
        displayName?: string;
        avatarMediaId?: string;
      };
    }>(res);
    return body.profile;
  }

  /**
   * Open a persistent WebSocket to the caller's NotificationRoom.
   * Server pushes a nudge message whenever a new invitation lands,
   * eliminating the invitation polling loop while the tab is open.
   */
  async openNotificationWebSocket(token: string): Promise<WebSocket> {
    const ticketRes = await this.#fetch(`${this.#baseUrl}/notifications/ws-ticket`, {
      method: 'POST',
      headers: this.#authJson(token),
    });
    const { ticket } = await this.#json<{ ticket: string }>(ticketRes);
    const wsUrl = new URL(`${this.#baseUrl}/notifications/ws`);
    wsUrl.protocol = wsUrl.protocol === 'https:' ? 'wss:' : 'ws:';
    wsUrl.searchParams.set('ticket', ticket);
    return new WebSocket(wsUrl.toString());
  }

  /**
   * Monotonic version of the user directory. Cheap (single KV read
   * server-side) — clients use this to short-circuit the hourly
   * profile-refresh when nothing has changed.
   */
  async getDirectoryVersion(token: string): Promise<number> {
    const res = await this.#fetch(`${this.#baseUrl}/users/directory-version`, {
      headers: { authorization: `Bearer ${token}` },
    });
    const body = await this.#json<{ version: number }>(res);
    return body.version;
  }

  async lookupUsers(
    token: string,
    userIds: readonly string[],
  ): Promise<
    Record<
      string,
      { userId: string; username?: string; displayName?: string; avatarMediaId?: string }
    >
  > {
    if (userIds.length === 0) return {};
    const res = await this.#fetch(`${this.#baseUrl}/users/by-ids`, {
      method: 'POST',
      headers: this.#authJson(token),
      body: JSON.stringify({ userIds }),
    });
    const body = await this.#json<{
      profiles: Record<
        string,
        { userId: string; username?: string; displayName?: string; avatarMediaId?: string }
      >;
    }>(res);
    return body.profiles;
  }

  async searchUsers(
    token: string,
    query: string,
    limit?: number,
  ): Promise<
    { userId: string; username?: string; displayName?: string; avatarMediaId?: string }[]
  > {
    const params = new URLSearchParams({ q: query });
    if (limit !== undefined) params.set('limit', String(limit));
    const res = await this.#fetch(`${this.#baseUrl}/users/search?${params.toString()}`, {
      headers: { authorization: `Bearer ${token}` },
    });
    const body = await this.#json<{
      results: {
        userId: string;
        username?: string;
        displayName?: string;
        avatarMediaId?: string;
      }[];
    }>(res);
    return body.results;
  }

  /**
   * Upload an avatar image. The server magic-byte-checks the bytes
   * (PNG / JPEG / GIF / WebP only) and stores the canonical MIME
   * alongside, so the receiver can render the bytes natively
   * without sniffing.
   */
  async uploadAvatar(
    token: string,
    bytes: Uint8Array,
  ): Promise<{ mediaId: string; mime: string; size: number }> {
    const buffer = new Uint8Array(new ArrayBuffer(bytes.length));
    buffer.set(bytes);
    const res = await this.#fetch(`${this.#baseUrl}/users/avatar`, {
      method: 'POST',
      headers: {
        authorization: `Bearer ${token}`,
        'content-type': 'application/octet-stream',
        'content-length': String(bytes.length),
      },
      body: buffer.buffer,
    });
    return this.#json<{ mediaId: string; mime: string; size: number }>(res);
  }

  /**
   * Fetch an avatar's bytes with its server-attested MIME. Different
   * from downloadMedia (which always returns octet-stream — the
   * V3 hardening for attacker-controlled attachment uploads); the
   * avatar route is locked to PNG/JPEG/GIF/WebP at upload time so
   * serving the actual MIME here is safe and lets the renderer skip
   * mime sniffing.
   */
  async downloadAvatar(
    token: string,
    userId: UserId,
    mediaId: string,
  ): Promise<{ bytes: Uint8Array; mime: string }> {
    const res = await this.#fetch(
      `${this.#baseUrl}/users/avatar/${encodeURIComponent(userId)}/${encodeURIComponent(mediaId)}`,
      { headers: { authorization: `Bearer ${token}` } },
    );
    if (!res.ok) {
      const text = await res.text();
      throw new ApiError(res.status, text || res.statusText);
    }
    const mime = res.headers.get('content-type') ?? 'image/png';
    const bytes = new Uint8Array(await res.arrayBuffer());
    return { bytes, mime };
  }

  async leaveMatchmaking(token: string, engineId: string): Promise<void> {
    const res = await this.#fetch(`${this.#baseUrl}/matchmaking/${encodeURIComponent(engineId)}`, {
      method: 'DELETE',
      headers: { authorization: `Bearer ${token}` },
    });
    await this.#json(res);
  }

  async uploadMedia(token: string, body: Uint8Array): Promise<{ mediaId: string; size: number }> {
    // Copy into a fresh ArrayBuffer so BodyInit typing (and SharedArrayBuffer
    // strictness) is satisfied across runtimes.
    const buf = new Uint8Array(new ArrayBuffer(body.length));
    buf.set(body);
    const res = await this.#fetch(`${this.#baseUrl}/media`, {
      method: 'POST',
      headers: {
        authorization: `Bearer ${token}`,
        'content-type': 'application/octet-stream',
      },
      body: buf,
    });
    return this.#json(res);
  }

  async downloadMedia(token: string, uploader: UserId, mediaId: string): Promise<Uint8Array> {
    const res = await this.#fetch(
      `${this.#baseUrl}/media/${encodeURIComponent(uploader)}/${encodeURIComponent(mediaId)}`,
      { headers: { authorization: `Bearer ${token}` } },
    );
    if (!res.ok) {
      const text = await res.text();
      throw new ApiError(res.status, text || res.statusText);
    }
    return new Uint8Array(await res.arrayBuffer());
  }

  /**
   * Remove `userId` from the conversation's server-side member list.
   * Caller must have already broadcast the MLS Remove commit so the
   * cryptographic state agrees. Any current member can invoke this.
   */
  async removeConversationMember(
    token: string,
    conversationId: ConversationId,
    userId: UserId,
  ): Promise<void> {
    const res = await this.#fetch(
      `${this.#baseUrl}/conversations/${encodeURIComponent(conversationId)}/members/${encodeURIComponent(userId)}`,
      { method: 'DELETE', headers: { authorization: `Bearer ${token}` } },
    );
    if (!res.ok) {
      const text = await res.text();
      throw new ApiError(res.status, text || res.statusText);
    }
  }

  /**
   * Tell the server to wipe a conversation for every member. Authorised
   * members only; other members find out on their next poll via a 403
   * from /history and drop the conversation locally.
   */
  async deleteConversation(token: string, conversationId: ConversationId): Promise<void> {
    const res = await this.#fetch(
      `${this.#baseUrl}/conversations/${encodeURIComponent(conversationId)}`,
      {
        method: 'DELETE',
        headers: { authorization: `Bearer ${token}` },
      },
    );
    if (!res.ok) {
      const text = await res.text();
      throw new ApiError(res.status, text || res.statusText);
    }
  }

  async fetchHistory(
    token: string,
    conversationId: ConversationId,
    since = 0,
  ): Promise<Array<Envelope & { sequence: number; receivedAt: number; senderUserId?: string }>> {
    const url = new URL(
      `${this.#baseUrl}/conversations/${encodeURIComponent(conversationId)}/history`,
    );
    url.searchParams.set('since', String(since));
    const res = await this.#fetch(url.toString(), {
      headers: { authorization: `Bearer ${token}` },
    });
    const body = await this.#json<{
      messages: Array<Envelope & { sequence: number; receivedAt: number; senderUserId?: string }>;
    }>(res);
    return body.messages;
  }

  /** Escape hatch used by CallsClient to share the configured fetch + base URL. */
  rawFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    const url =
      typeof input === 'string' && input.startsWith('/') ? `${this.#baseUrl}${input}` : input;
    return this.#fetch(url, init);
  }

  async fetchWsTicket(
    token: string,
    conversationId: string,
  ): Promise<{ ticket: string; expiresAt: number }> {
    const res = await this.#fetch(
      `${this.#baseUrl}/conversations/${encodeURIComponent(conversationId)}/ws-ticket`,
      { method: 'POST', headers: { authorization: `Bearer ${token}` } },
    );
    return this.#json<{ ticket: string; expiresAt: number }>(res);
  }

  async openWebSocket(token: string, conversationId: string): Promise<WebSocket> {
    // Fetch a short-lived scoped ticket so the 24-hour session token
    // never lands on a URL that gets captured by browser devtools,
    // Cloudflare access logs, or reverse-proxy pipelines. The ticket
    // is scoped to this conversationId + 30-second TTL.
    const { ticket } = await this.fetchWsTicket(token, conversationId);
    const httpUrl = new URL(
      `${this.#baseUrl}/conversations/${encodeURIComponent(conversationId)}/ws`,
    );
    const wsUrl = new URL(httpUrl.toString());
    wsUrl.protocol = wsUrl.protocol === 'https:' ? 'wss:' : 'ws:';
    wsUrl.searchParams.set('ticket', ticket);
    return new WebSocket(wsUrl.toString());
  }

  #authJson(token: string): Record<string, string> {
    return {
      'content-type': 'application/json',
      authorization: `Bearer ${token}`,
    };
  }

  async #json<T>(res: Response): Promise<T> {
    const text = await res.text();
    if (!res.ok) {
      let code: string | undefined;
      let message = text || res.statusText;
      try {
        const parsed = JSON.parse(text) as { error?: string; code?: string };
        if (parsed.error) message = parsed.error;
        code = parsed.code;
      } catch {
        // keep text
      }
      throw new ApiError(res.status, message, code);
    }
    if (!text) return undefined as T;
    return JSON.parse(text) as T;
  }

  // re-exported for convenience in clients
  static toBase64Url = toBase64Url;
  static fromBase64Url = fromBase64Url;
}
